// Powers: abilities a standing structure grants (§26 F6) — **and, since §31
// A0, its gear on a Monarch**: `hasPower` is structure ∧ equipment
// (`poweredCount`, `campaign/equipment.ts`).
//
// A card is a thing you *hold and spend*. A power is a thing you *have* — for
// as long as **a structure of its kind stands under your control**. That is
// the whole of what replaced `run.powers`: a power used to be permanent from
// the moment its artifact reached your king, and permanence with no address is
// what the old hard edge ("nothing permanent may grant tempo or material")
// existed to fence. Now the power is a board object the enemy can take, and
// the fence is the board itself: `hasPower` asks it, capture ends the power at
// once, and placement is the price — a structure projects territory, so
// forward is useful and contestable, deep is safe and projects nothing new.
//
// Powers are still deliberately not cards with `reusable: true`. A reusable
// card occupies a hand slot, has to be clicked to "play", and reads as
// something you might run out of. A power is none of those things, and the
// difference is legible on screen: cards are in your hand, powers are on the
// wall — pips derived from the structures standing.

import type { Color } from "../board";
import {
  ALTAR,
  ARTISAN_PILLAR,
  ENDURANCE_MONUMENT,
  FIEND_FOUNTAIN,
  FUSION_CHAMBER,
  HOUSE_OF_CARDS,
  LEGION_TOWER,
  PROPAGANDA_TOWER,
  KEYSTONE,
  RELIC,
  SHIELD_GENERATOR,
  WELL,
  type IndustryState,
  type StructureKind
} from "../industry/state";
import { poweredCount } from "./equipment";

/**
 * **A power spends the turn. That is the default, and a new one has to argue
 * its way out of it rather than into it.**
 *
 * A power is permanent and free to *hold*, so the only price it can carry is
 * the turn it is used on — and without one it is a board change with no cost,
 * which every player takes every turn it is available. There is no decision in
 * an ability that is strictly better than not using it.
 *
 * Sacrifice is the worked example and it shipped the wrong way round: it
 * changed a body into metal and passed the clock to nobody, so the optimal
 * play was to melt something every single turn until there was nothing beside
 * the king. It costs the turn now (`passTurn` + `endTurn`, exactly as `forge`
 * does), which is also what makes it a *candidate turn* the planner can weigh
 * against a move rather than a free action it should always take.
 *
 * The exception is real but narrow, and names itself: **Emplacement** has no
 * gesture of its own — it unlocks a thing to build, and `build` already costs
 * the turn. Coronation was another and is a card now (§26 C5), free — what a
 * card pays instead is being spent.
 *
 * The shape to copy for anything new: cost the turn in the reducer, then add a
 * candidate generator beside `forgeCandidates` so the opponent can choose it
 * against a move. A power the planner cannot cost is a power it never uses.
 */

/** Every power. Ordinals are not the wire format — the id is. */
// **`coronation`, `observation-chamber` and `marching-orders` are gone**:
// coronation is a card since §26 C5; the chamber and the banner's standing
// orders are retired (§31 R1).
export const POWERS = [
  "sacrifice",
  "emplacement",
  "industrial-titan",
  // §31 A4: the Oil-Stone Scepter — the Sacred Well and its Scepter.
  "oil-stone",
  // §31 A8: the Fusion Chamber and the Signet of Union.
  "fusion",
  // §31 A9: Deflector Shields — the Shield Generator and its badge.
  "deflector",
  // §31 A6: the Artisan Pillar — the Pillar and the Mark of Excellence.
  "artisan",
  // §31 A10–A13: Stoke the Forges, My Deck Runith Over, Yield Not, Flow Faster.
  "stoke",
  "deck",
  "defiance",
  "flow",
  // §33 T5: the Legion Mandate — the Legion Tactics Tower and its pin.
  "legion"
] as const;
export type PowerId = (typeof POWERS)[number];

/**
 * Which structure kind carries each power (§26 F6).
 *
 * A power is **on while a structure of its kind stands under your control** —
 * `run.powers` is gone, and this table plus `hasPower` below is the whole of
 * what replaced it. The governing principle, recorded in the plan so it
 * survives the migration: the old hard edge "nothing permanent may grant tempo
 * or material" is replaced, for structures, by *"a permanent power is a board
 * object the enemy can take."* The Relic grants the loudest tempo in the game
 * and is acceptable because it has an address.
 */
export const POWER_KIND: Record<PowerId, StructureKind> = {
  sacrifice: ALTAR,
  emplacement: KEYSTONE,
  "industrial-titan": RELIC,
  "oil-stone": WELL,
  fusion: FUSION_CHAMBER,
  deflector: SHIELD_GENERATOR,
  artisan: ARTISAN_PILLAR,
  stoke: PROPAGANDA_TOWER,
  deck: HOUSE_OF_CARDS,
  defiance: ENDURANCE_MONUMENT,
  flow: FIEND_FOUNTAIN,
  legion: LEGION_TOWER
};

/**
 * Is this power on for this side, right now?
 *
 * Asks the board and nothing else, which is what makes "a captured artifact
 * ends its power at once" true by construction: the capture wrecks the
 * structure, so the very next read answers false — no membership list to keep
 * in step, no event to forget to fire.
 *
 * **Titan's *amount* is the same question counted** (§26 A2): whether the
 * side has it is a Relic standing, and how many moves and ticks it grants is
 * how many stand — `relicsOf` in `campaign/titan.ts`, which is this loop as a
 * count.
 */
export function hasPower(
  industry: IndustryState,
  side: Color,
  power: PowerId
): boolean {
  // §31 A0: structure ∧ equipment — `poweredCount` asks both.
  return poweredCount(industry, side, POWER_KIND[power]) > 0;
}

/**
 * How many Targeting Arrays `side` has powered (§31 A7): Mainframe standing ∧
 * Turret Manager Badge borne — `poweredCount`. One is the gate to build a
 * turret; **each one past the first makes every turret stronger** (the
 * design's "+2 attacks per extra array", S8 reads it). No cap — §26 M2's
 * `turretsPerKeystone` is retired: the design limits no turret count.
 */
export function targetingArrays(industry: IndustryState, side: Color): number {
  return poweredCount(industry, side, KEYSTONE);
}

export interface Power {
  id: PowerId;
  name: string;
  /** One line, on the pip's tooltip. */
  text: string;
  /** A single character, because a pip is a pip. */
  glyph: string;
}

export const POWER_TABLE: Record<PowerId, Power> = {
  /**
   * A *valuation*, not an event — which is why it is a power rather than a
   * card. Once you have it, every piece you own is also an ingot of a known
   * grade, and that is a way of reading the board rather than a thing you
   * spend.
   *
   * It is the exception that tests "no power may hand out material", and it
   * survives on two counts: what it returns is always less than the piece
   * cost, and the one loop that beats that exchange rate runs through
   * promotion, which is finite and authored.
   */
  /**
   * Turrets, and the whole tower-defence layer with them.
   *
   * Behind a power because it is the one part of §14 that is **not core**: it
   * adds a second game, played with material against arriving armies, and
   * that is not something to hand a player still learning that a bishop is an
   * oven. It drops late and for a stated reason — by then you should already
   * have the problem it answers, which is too many armies on the board at
   * once.
   */
  emplacement: {
    id: "emplacement",
    // §31 A7: the Targeting Array — a Mainframe and its Turret Manager Badge.
    name: "Targeting Array",
    text:
      "Build turrets. Every Targeting Array past the first " +
      "makes your turrets stronger.",
    glyph: "⌖"
  },
  /**
   * A move a turn, for the rest of the run, and it stacks.
   *
   * **A power rather than a card because what it changes is the shape of a
   * turn**, not a moment in one. The Reactor Boosters are the card version of
   * the same arithmetic and expire with the turn they were played on; this one
   * is the
   * reason a large army can still be played. It is also **an economy grant,
   * permanently**: every move runs
   * the factory for every living side, so each copy is a standing extra tick
   * for everybody on the board, not only for the holder.
   */
  "industrial-titan": {
    id: "industrial-titan",
    name: "Industrial Titan",
    text:
      "One more move every turn, for the rest of the run — and another for " +
      "each one you find. Your factory ticks with every one.",
    glyph: "⏩"
  },
  sacrifice: {
    id: "sacrifice",
    name: "Sacrifice",
    text:
      "Take one of your pieces apart beside your altar or your Kris-bearer. " +
      "Half its bill comes back, at the grades it was made of, and a bonus " +
      "factory tick. Once a turn.",
    glyph: "⚱"
  },
  "oil-stone": {
    id: "oil-stone",
    name: "Oil-Stone Scepter",
    text:
      "Play an enemy faction's next turn — its legal moves only. Once every " +
      "five rounds while the Sacred Well and the Scepter stand.",
    glyph: "♆"
  },
  fusion: {
    id: "fusion",
    name: "Fusion Chamber",
    text:
      "Fuse two pieces of yours into one, beside a forger or within 2 of an " +
      "Armory or the Chamber. The Chamber is an Armory besides.",
    glyph: "⚭"
  },
  deflector: {
    id: "deflector",
    name: "Deflector Shields",
    text:
      "Fed 5 fuel points a round, every piece of yours and your allies' in " +
      "reach takes 1 less damage from anything — up to 3 generators stack.",
    glyph: "⛨"
  },
  artisan: {
    id: "artisan",
    name: "Artisan Pillar",
    text:
      "+1 HP and 2 HP a round for every piece of yours, upgrades one cheaper, " +
      "Monarchs reach 2 further. Lose Pillar or Mark and it is gone for the " +
      "run: −1 max HP forever, and the bearer dies.",
    glyph: "♕"
  },
  legion: {
    id: "legion",
    name: "Legion Mandate",
    text:
      "+1 Army Logistics: each Power Move brings one more Fodder Move. " +
      "Two at most.",
    glyph: "⚔"
  },
  stoke: {
    id: "stoke",
    name: "Stoke the Forges",
    text:
      "+1 Off-Turn Production point: at each round's close your factory runs " +
      "a tick per enemy faction alive that round. One per side.",
    glyph: "⚑"
  },
  deck: {
    id: "deck",
    name: "My Deck Runith Over",
    text:
      "The bearer's captures hit 2 harder, and every 20 of your factory ticks " +
      "earn a card reward — a choice of three.",
    glyph: "♠"
  },
  defiance: {
    id: "defiance",
    name: "Yield Not",
    text:
      "The bearer wears 10 more armor; your obstructions have 3 more HP and " +
      "your other buildings 1 more, while you hold it.",
    glyph: "⛉"
  },
  flow: {
    id: "flow",
    name: "Flow Faster",
    text: "Factory Flow does not bind you: forge and move on the same turn.",
    glyph: "⛲"
  }
};

export const POWER_LIST: readonly Power[] = POWERS.map((id) => POWER_TABLE[id]);

export const isPower = (id: string): id is PowerId =>
  (POWERS as readonly string[]).includes(id);
